import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { commandCenterApi } from '../lib/commandCenterApi';
import type { CommandCenter, ClassRow } from '../lib/commandCenterApi';
import type { DuesInsight } from '../lib/types';
import { Link } from 'react-router-dom';
import { classKeyOf } from '../lib/resultsApi';
import { PresenceStrip } from '../components/PresenceStrip';
import { EligibilityAlert } from '../components/EligibilityAlert';
import { useTenant } from '../context/TenantContext';
import {
  ChartFrame,
  Donut,
  HBarChart,
  LineChart,
  Meter,
  StatTile,
  pct,
  rupeesFull,
  rupeesShort,
} from '../components/charts';

/**
 * THE TRUSTEE / PRINCIPAL COMMAND CENTRE — the first screen leadership opens.
 *
 * ⚠ WHY THIS PAGE EXISTS. Two prospects walked away calling Eduvora "a legacy ERP" because the
 * landing screen was one paragraph of fee prose. Their words: "if I enter as trustee or principal I
 * always expect a complete overview — how many students in each class, how much fee collected in
 * each class, attendance of each class, and who are the top 3." Every one of those numbers was
 * already in the database. None of them reached a screen.
 *
 * ⚠ FIRST PAINT IS DETERMINISTIC. The dashboard renders entirely from one cached-free aggregate
 * call. The AI narrative is fetched SEPARATELY and lands afterwards. An LLM round-trip in the
 * first-paint path means a spinner in front of a trustee, and a rate-limit means a blank dashboard
 * mid-pitch. Deterministic first, clever second.
 *
 * ⚠ ATTENDANCE null IS NOT ZERO. A class with no marked attendance shows "—". Rendering it as 0%
 * tells a principal their school did not turn up.
 */
export function CommandCenterPage() {
  const { branding } = useTenant();

  const [data, setData] = useState<CommandCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [year, setYear] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState<number | null>(null);

  // AI narrative — deliberately a second, independent request.
  const [dues, setDues] = useState<DuesInsight | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400000);
    const iso = (d: Date) => d.toISOString().slice(0, 10);

    commandCenterApi
      .load({ institutionId, year, from: iso(from), to: iso(to) })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setFailed(false);
        setSelected(null);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [institutionId, year, days]);

  useEffect(() => {
    let alive = true;
    api
      .get<DuesInsight>('/api/ai/fees/dues-summary')
      .then((d) => alive && setDues(d))
      .catch(() => {
        /* AI is optional here by design — the numbers above never depend on it. */
      });
    return () => {
      alive = false;
    };
  }, [institutionId, year]);

  const classes = data?.classes ?? [];

  // Worst-collecting classes first is the actionable order for a principal; the toggle
  // restores natural class order for people who just want to find "8-A".
  const [worstFirst, setWorstFirst] = useState(false);
  const ordered = useMemo(() => {
    if (!worstFirst) return classes;
    return [...classes].sort((a, b) => a.collectedPct - b.collectedPct);
  }, [classes, worstFirst]);

  const shown = ordered.slice(0, 14);
  const active: ClassRow | null = selected === null ? null : shown[selected] ?? null;

  if (loading && !data) {
    return <p style={{ color: 'var(--ink-3)' }}>Loading the school…</p>;
  }

  if (failed || !data) {
    return (
      <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
        Could not load the overview.
      </div>
    );
  }

  const t = data.totals;

  const feeSeries = [
    { name: 'Collected', color: 'var(--s1)', values: shown.map((c) => c.collectedPaise) },
    { name: 'Outstanding', color: 'var(--s2)', values: shown.map((c) => c.outstandingPaise) },
  ];

  const trendSeries = [
    { name: 'Billed', color: 'var(--s2)', values: data.months.map((m) => m.billedPaise) },
    { name: 'Collected', color: 'var(--s1)', values: data.months.map((m) => m.collectedPaise) },
  ];

  const attendanceSeries = [
    {
      name: 'Attendance',
      color: 'var(--s3)',
      values: shown.map((c) => c.attendancePct ?? 0),
    },
  ];

  return (
    <div>
      {/* ---------------- header ---------------- */}
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>
          {branding?.name ?? 'Overview'}
        </div>
        <h1 className="h-display" style={{ fontSize: 28 }}>
          {greeting()} — here is your school today
        </h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Every figure is computed live from the ledger, the register and finalized report cards.
          Attendance covers the last {days} days.
        </p>
      </div>

      {/* ---------------- filters: ONE row, above everything they scope ---------------- */}
      <div
        className="tab-strip"
        style={{ gap: 8, marginBottom: 18, borderBottom: 'none', alignItems: 'center' }}
      >
        {data.institutions.length > 1 && (
          <select
            className="input"
            value={institutionId ?? ''}
            onChange={(e) => setInstitutionId(e.target.value || null)}
            style={{ minWidth: 170 }}
          >
            <option value="">All institutions</option>
            {data.institutions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} · {i.kind}
              </option>
            ))}
          </select>
        )}

        {data.availableYears.length > 0 && (
          <select
            className="input"
            value={year ?? ''}
            onChange={(e) => setYear(e.target.value || null)}
            style={{ minWidth: 130 }}
          >
            <option value="">All years</option>
            {data.availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        )}

        <select
          className="input"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ minWidth: 150 }}
        >
          <option value={7}>Attendance: 7 days</option>
          <option value={30}>Attendance: 30 days</option>
          <option value={90}>Attendance: 90 days</option>
          <option value={365}>Attendance: this year</option>
        </select>
      </div>

      {/* ⚠ While a filter refetches, the previous render stays on screen at reduced opacity —
          no skeleton, no layout jump. The numbers never flash to zero. */}
      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        {/* ---------------- headline tiles ---------------- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Students on roll"
              value={t.students.toLocaleString('en-IN')}
              foot={`${t.classes} classes · ${t.staff} staff`}
            />
          </div>

          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Fee collected"
              value={rupeesShort(t.collectedPaise)}
              foot={
                <>
                  {t.collectedPct.toFixed(1)}% of {rupeesShort(t.billedPaise)} billed
                  <div style={{ marginTop: 6 }}>
                    <Meter value={t.collectedPct} color="var(--s1)" />
                  </div>
                </>
              }
            />
          </div>

          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Outstanding"
              value={rupeesShort(t.outstandingPaise)}
              foot={
                <span style={{ color: t.overduePaise > 0 ? 'var(--danger)' : undefined }}>
                  {rupeesShort(t.overduePaise)} overdue · {t.studentsWithDues} students
                </span>
              }
            />
          </div>

          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label={`Attendance (${days}d)`}
              value={pct(t.attendancePct)}
              foot={
                t.attendancePct === null ? (
                  'Not marked in this window'
                ) : (
                  <>
                    {t.attendancePresent.toLocaleString('en-IN')} of{' '}
                    {t.attendanceHeld.toLocaleString('en-IN')} marks
                    <div style={{ marginTop: 6 }}>
                      <Meter
                        value={t.attendancePct}
                        color={t.attendancePct >= 90 ? 'var(--ok)' : t.attendancePct >= 75 ? 'var(--warn)' : 'var(--danger)'}
                      />
                    </div>
                  </>
                )
              }
            />
          </div>
        </div>

        {/* ---------------- AI narrative (lands after the numbers) ---------------- */}
        {dues && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span className="pill pill-muted" style={{ fontSize: 10.5 }}>
                AI summary
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                written from the figures above — never recalculated
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.6 }}>
              {dues.narrative}
            </p>
          </div>
        )}

        {/* ---------------- trend + share ---------------- */}
        {/* ⚠ FIRST on the page when it renders at all, and invisible when it does not. Being
            barred from an examination is the most consequential thing on this dashboard and it has
            a deadline attached; the fee curve does not. It takes the institution currently selected
            above, so switching from the school to the college switches the rule with it. */}
        <EligibilityAlert institutionId={data.institutionId} />

        {/* ⚠ ABOVE the money split on purpose. "Which of my teachers is out and which section has
            nobody in front of it" is the first thing a head of school acts on in the morning; the
            collection curve is the first thing they REPORT on. Those are not the same priority, and
            the order of the page should say which is which. */}
        <PresenceStrip />

        <div className="cc-split" style={{ marginBottom: 16 }}>
          <div className="card" style={{ padding: 16, minWidth: 0 }}>
            <ChartFrame
              title="Billing and collection, last 12 months"
              subtitle="Where the gap between the two widens is where the follow-up went quiet."
              series={trendSeries}
              legendAsLine
              table={
                <table className="viz-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="num">Billed</th>
                      <th className="num">Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((m) => (
                      <tr key={m.month}>
                        <td>{m.month}</td>
                        <td className="num">{rupeesFull(m.billedPaise)}</td>
                        <td className="num">{rupeesFull(m.collectedPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              }
            >
              <LineChart
                labels={data.months.map((m) => m.month)}
                series={trendSeries}
                format={rupeesShort}
              />
            </ChartFrame>
          </div>

          <div className="card" style={{ padding: 16, minWidth: 0 }}>
            <ChartFrame
              title="Where the year's fees stand"
              series={[
                { name: 'Collected', color: 'var(--s1)', values: [] },
                { name: 'Outstanding', color: 'var(--s2)', values: [] },
              ]}
            >
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
                <Donut
                  slices={[
                    { name: 'Collected', value: t.collectedPaise, color: 'var(--s1)' },
                    { name: 'Outstanding', value: t.outstandingPaise, color: 'var(--s2)' },
                  ]}
                  format={rupeesFull}
                  centerValue={`${t.collectedPct.toFixed(0)}%`}
                  centerLabel="collected"
                />
              </div>
            </ChartFrame>
          </div>
        </div>

        {/* ---------------- per class ---------------- */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            <div className="viz-title" style={{ fontSize: 13 }}>
              Fees by class
            </div>
            <button
              type="button"
              className="viz-table-toggle"
              onClick={() => setWorstFirst((v) => !v)}
            >
              {worstFirst ? 'Show in class order' : 'Show worst collection first'}
            </button>
          </div>
          <p className="viz-sub">
            Hover or tap any class for its strength, attendance and top three. Click to pin it below.
            {classes.length > shown.length && ` Showing ${shown.length} of ${classes.length} classes.`}
          </p>

          <ChartFrame
            title=""
            series={feeSeries}
            table={
              <table className="viz-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th className="num">Students</th>
                    <th className="num">Billed</th>
                    <th className="num">Collected</th>
                    <th className="num">Outstanding</th>
                    <th className="num">Collected %</th>
                    <th className="num">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((c) => (
                    <tr key={c.classLabel}>
                      <td>{c.classLabel}</td>
                      <td className="num">{c.strength}</td>
                      <td className="num">{rupeesFull(c.billedPaise)}</td>
                      <td className="num">{rupeesFull(c.collectedPaise)}</td>
                      <td className="num">{rupeesFull(c.outstandingPaise)}</td>
                      <td className="num">{c.collectedPct.toFixed(1)}%</td>
                      <td className="num">{pct(c.attendancePct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <HBarChart
              categories={shown.map((c) => c.classLabel)}
              series={feeSeries}
              format={rupeesShort}
              activeIndex={selected}
              onSelect={(i) => setSelected((cur) => (cur === i ? null : i))}
              extraTip={(i) => {
                const c = shown[i];
                if (!c) return [];
                const rows = [
                  { name: 'Students', value: String(c.strength), color: 'transparent' },
                  { name: 'Attendance', value: pct(c.attendancePct), color: 'transparent' },
                ];
                c.toppers.forEach((tp, n) =>
                  rows.push({
                    name: `${n + 1}. ${tp.studentName}`,
                    value: `${tp.percentage.toFixed(1)}%`,
                    color: 'transparent',
                  }),
                );
                return rows;
              }}
            />
          </ChartFrame>
        </div>

        {/* ---------------- attendance by class ---------------- */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <ChartFrame
            title={`Attendance by class, last ${days} days`}
            subtitle="Excused absences leave the denominator; a late mark counts as present."
            table={
              <table className="viz-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th className="num">Present</th>
                    <th className="num">Held</th>
                    <th className="num">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((c) => (
                    <tr key={c.classLabel}>
                      <td>{c.classLabel}</td>
                      <td className="num">{c.attendancePresent}</td>
                      <td className="num">{c.attendanceHeld}</td>
                      <td className="num">{pct(c.attendancePct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <HBarChart
              categories={shown.map((c) => c.classLabel)}
              series={attendanceSeries}
              format={(n) => (n === 0 ? '—' : `${n.toFixed(1)}%`)}
              onSelect={(i) => setSelected((cur) => (cur === i ? null : i))}
              activeIndex={selected}
              /* ⚠ Status colour, and legal here only because every bar is directly labelled
                 with its own percentage — the hue is a second signal, never the only one. */
              colorAt={(i) => {
                const p = shown[i]?.attendancePct;
                if (p === null || p === undefined) return 'var(--ink-3)';
                return p >= 90 ? 'var(--ok)' : p >= 75 ? 'var(--warn)' : 'var(--danger)';
              }}
            />
          </ChartFrame>
        </div>

        {/* ---------------- pinned class detail ---------------- */}
        {active && (
          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap',
              }}
            >
              <h2 className="h-display" style={{ fontSize: 20, margin: 0 }}>
                {active.classLabel}
              </h2>
              <span style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
                {/* ⚠ The click-through the principal asked for: from "which class" to the actual
                    mark sheet for that class, carrying the same class key the server groups on. */}
                <Link
                  to={`/results?class=${encodeURIComponent(classKeyOf(active.academicClassId, active.classLabel))}`}
                  className="viz-table-toggle"
                  style={{ textDecoration: 'underline' }}
                >
                  See full results
                </Link>
                <button type="button" className="viz-table-toggle" onClick={() => setSelected(null)}>
                  Close
                </button>
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 14,
              }}
            >
              <StatTile label="Strength" value={String(active.strength)} />
              <StatTile
                label="Collected"
                value={rupeesShort(active.collectedPaise)}
                foot={`${active.collectedPct.toFixed(1)}% of ${rupeesShort(active.billedPaise)}`}
              />
              <StatTile
                label="Outstanding"
                value={rupeesShort(active.outstandingPaise)}
                foot={`${active.studentsWithDues} students · ${active.overdueStudents} overdue`}
              />
              <StatTile
                label="Attendance"
                value={pct(active.attendancePct)}
                foot={
                  active.attendancePct === null
                    ? 'Not marked in this window'
                    : `${active.attendancePresent} of ${active.attendanceHeld} marks`
                }
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <div className="viz-title" style={{ marginBottom: 8 }}>
                Top of the class
              </div>
              {active.toppers.length === 0 ? (
                <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>
                  No finalized report cards for this class yet. Draft cards are deliberately
                  excluded — a parent has not seen them, so nobody should be ranked on them.
                </p>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  {active.toppers.map((tp) => (
                    <li key={tp.studentId} style={{ fontSize: 13.5, marginBottom: 4 }}>
                      <strong>{tp.studentName}</strong>{' '}
                      <span style={{ color: 'var(--ink-2)' }}>
                        — {tp.percentage.toFixed(1)}% ({tp.termName})
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}

        {/* ---------------- cross-board caveat ---------------- */}
        {data.caveat && (
          <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, marginTop: 4 }}>
            ⚠ {data.caveat}
          </p>
        )}

        {classes.length === 0 && (
          <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
            No active students in this view yet. Once students are enrolled this screen fills in on
            its own — nothing here needs configuring.
          </div>
        )}
      </div>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
